import { Stack, ButtonBase, styled } from '@mui/material';
import { useIntl } from 'react-intl';
import { useCallback } from 'react';
import { useConfiguration } from '@ocx-app/modules/configuration/useConfiguration';
import { useAsset } from '@ocx-app/modules/configuration/use-asset.hook';
import { Typography } from '@ocx/ui';

import { homeMessages } from '../home.messages';
import { useGetNavigationActions } from '../../../hooks/useGetNavigationActions';
import { SizingMultiplier } from '../../../theme/constants';
import { useRudderStack } from '../../../lib/rudderStack/useRudderStack';
import { formatPrice } from '../../../lib/formatters/formatPrice';

const StyledButtonBase = styled(ButtonBase)(({ theme: { palette, shape, spacing } }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: palette.background.paper,
  borderRadius: shape.borderRadius * 4,
  padding: spacing(SizingMultiplier.sm, 0, SizingMultiplier.sm, SizingMultiplier.md),
}));

export type RewardsControllerProps = {
  rewardsQuantity: number;
  totalAvailableRollbackMoneyAmount?: number;
};

export const RewardsController = ({ rewardsQuantity, totalAvailableRollbackMoneyAmount }: RewardsControllerProps) => {
  const intl = useIntl();
  const { config } = useConfiguration();
  const { pushToReadyToUseOffers, pushToTotalAvailableRollbackOffers } = useGetNavigationActions();
  const { triggerEvent } = useRudderStack();

  const readyToUseRewardsSrc = useAsset('ReadyToUseRewards');
  const totalAvailableRollbackSrc = useAsset('TotalAvailableRollback');

  const handleReadyToUseClick = useCallback(() => {
    triggerEvent('ready_to_use_clicked');
    pushToReadyToUseOffers();
  }, [pushToReadyToUseOffers, triggerEvent]);

  const moneyAmount = formatPrice(totalAvailableRollbackMoneyAmount || 0, {
    displayCentsWhenLessThanDollar: true,
  });
  const showTotalAvailableRollback = config.totalAvailableRollback.enabled && totalAvailableRollbackMoneyAmount;

  if (!rewardsQuantity && !showTotalAvailableRollback) {
    return null;
  }

  return (
    <Stack direction="row" spacing={SizingMultiplier.sm} useFlexGap>
      {showTotalAvailableRollback ? (
        <StyledButtonBase onClick={pushToTotalAvailableRollbackOffers}>
          <Typography variant="subtitle1">
            {rewardsQuantity
              ? intl.formatMessage(homeMessages['home:rewards:totalAvailableRollback'], { moneyAmount })
              : intl.formatMessage(homeMessages['home:rewards:totalAvailableRollbackFull'], { moneyAmount })}
          </Typography>
          <img src={totalAvailableRollbackSrc} alt="rewards icon" width={56} height={48} />
        </StyledButtonBase>
      ) : null}
      {rewardsQuantity ? (
        <StyledButtonBase onClick={handleReadyToUseClick}>
          <Typography variant="subtitle1">
            {showTotalAvailableRollback
              ? intl.formatMessage(homeMessages['home:rewards:readyToUseRewards'], { rewardsQuantity })
              : intl.formatMessage(homeMessages['home:rewards:readyToUseRewardsFull'], { rewardsQuantity })}
          </Typography>
          <img src={readyToUseRewardsSrc} alt="rewards icon" width={56} height={48} />
        </StyledButtonBase>
      ) : null}
    </Stack>
  );
};
